import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { GroupedBarChart } from '@carbon/charts-react';
import { green, red, blue, warmGray } from '@carbon/colors';
import { InventoryMovementTypeEnum, InventoryMovementTypes } from 'src/utils/inventory';
import { useChartTheme } from './useChartTheme';

interface MovementsPerDayProps {
  title: string;
  movementsPerDay: Array<{ bucketKey: string; movementType: number; quantity: number }>;
  dateFormat: string;
}

const MovementsPerDay = React.memo((props: MovementsPerDayProps) => {
  const { t } = useTranslation('dashboard');
  const chartTheme = useChartTheme();
  const transformedMovementTypes = InventoryMovementTypes.reduce((previousObject, currentObject) => {
    return Object.assign(previousObject, {
      [currentObject.value]: t(`translation:${currentObject.label}`),
    });
  }, {});

  const getScaleOptions = () => {
    const options = {};
    props.movementsPerDay.map((item) => {
      if (
        !options[transformedMovementTypes[item.movementType]] &&
        item.movementType === InventoryMovementTypeEnum.RECEIVE
      ) {
        options[transformedMovementTypes[InventoryMovementTypeEnum.RECEIVE]] = green[50];
      }
      if (
        !options[transformedMovementTypes[item.movementType]] &&
        item.movementType === InventoryMovementTypeEnum.USAGE
      ) {
        options[transformedMovementTypes[InventoryMovementTypeEnum.USAGE]] = blue[50];
      }
      if (
        !options[transformedMovementTypes[item.movementType]] &&
        item.movementType === InventoryMovementTypeEnum.WASTE
      ) {
        options[transformedMovementTypes[InventoryMovementTypeEnum.WASTE]] = red[60];
      }
      if (
        !options[transformedMovementTypes[item.movementType]] &&
        item.movementType === InventoryMovementTypeEnum.ADJUST
      ) {
        options[transformedMovementTypes[InventoryMovementTypeEnum.ADJUST]] = warmGray[50];
      }
    });
    return options;
  };

  return (
    <div className="dashboard__stat_widget">
      <GroupedBarChart
        data={props.movementsPerDay.map((item) => ({
          group: transformedMovementTypes[item.movementType],
          date: item.bucketKey,
          value: item.quantity ?? 0,
        }))}
        options={{
          title: props.title,
          axes: {
            left: {
              mapsTo: 'value',
            },
            bottom: {
              mapsTo: 'date',
              scaleType: 'time' as any,
              ticks: {
                formatter: (date) => {
                  if (
                    dayjs(date).format(props.dateFormat) === dayjs().format(props.dateFormat) &&
                    props.dateFormat === 'DD/MM'
                  ) {
                    return `Today (${dayjs(date).format(props.dateFormat)})`;
                  }
                  return dayjs(date).format(props.dateFormat || 'DD/MM');
                },
              },
            },
          },
          color: {
            pairing: {
              option: 2,
            },
            scale: getScaleOptions(),
          },
          experimental: true,
          height: '400px',
          zoomBar: {
            top: {
              enabled: true,
              type: 'slider_view' as any,
              initialZoomDomain:
                props.dateFormat === 'MM/YYYY'
                  ? [dayjs().subtract(7, 'month').toDate(), dayjs().add(1, 'month').toDate()]
                  : [dayjs().subtract(7, 'day').toDate(), dayjs().add(1, 'day').toDate()],
            },
          },
          theme: chartTheme,
        }}
      />
    </div>
  );
});

export default MovementsPerDay;
